
import { Card } from "@/components/ui/card";
import { format } from 'date-fns';
import { useTask } from '@/contexts/TaskContext';

interface CompletedTask {
  title: string;
  completedAt: string;
}

interface Task {
  id: string;
  title: string;
  duration: number;
  scheduledTime: string;
  completed: boolean;
  completedAt?: string;
  createdAt: string;
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  completedTasks?: CompletedTask[];
  onClick?: () => void;
}

export default function FeatureCard({ 
  icon, 
  title, 
  description, 
  completedTasks = [],
  onClick 
}: FeatureCardProps) {
  const { tasks, completedTasks: contextCompletedTasks, activeTasks } = useTask();

  // Convert task data to display format
  const completedTasksForDisplay = contextCompletedTasks.map(task => ({
    title: task.title,
    completedAt: task.completedAt || task.createdAt
  }));

  const activeTasksForDisplay = activeTasks.map(task => ({
    title: task.title,
    completedAt: task.scheduledTime // Keep as time string for display
  }));

  // Determine which tasks to display based on the card title
  const tasksToDisplay = title === "Completed Tasks" ? 
    (completedTasks.length > 0 ? completedTasks : completedTasksForDisplay) : 
    title === "Today's Task" ? 
    activeTasksForDisplay : 
    [];

  const handleCardClick = () => {
    console.log(`${title} card clicked, showing ${tasksToDisplay.length} tasks`);
    if (onClick) {
      onClick();
    }
  };

  return (
    <Card 
      className="p-4 bg-white/80 hover:bg-white/90 transition-all duration-200 cursor-pointer card-hover glass-panel"
      onClick={handleCardClick}
    >
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-primary/10 rounded-full">
              {icon}
            </div>
            <h3 className="font-medium text-primary">{title}</h3>
          </div>
          {title === "Today's Task" && ( 
            <span className="text-xs text-muted-foreground bg-primary/5 px-2 py-1 rounded-full">
              {activeTasks.length}
            </span>
          )}
          {title === "Completed Tasks" && (
            <span className="text-xs text-muted-foreground bg-primary/5 px-2 py-1 rounded-full">
              {contextCompletedTasks.length}
            </span>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground">{description}</p>
        
        <div className="mt-2 space-y-1 max-h-24 overflow-y-auto">
          {tasksToDisplay.slice(0, 3).map((task, index) => (
            <div key={index} className="flex items-center justify-between text-sm px-2 py-1 rounded bg-white/60">
              <span className="font-medium truncate max-w-[80%]">{task.title}</span>
              <span className="text-xs text-muted-foreground">
                {task.completedAt && (() => {
                  try {
                    // Handle different date formats safely
                    const date = new Date(task.completedAt);
                    return isNaN(date.getTime()) ? task.completedAt : format(date, 'h:mm a');
                  } catch {
                    return task.completedAt;
                  }
                })()}
              </span>
            </div>
          ))}
          
          {tasksToDisplay.length > 3 && (
            <div className="text-xs text-muted-foreground text-center pt-1">
              +{tasksToDisplay.length - 3} more
            </div>
          )}
          
          {tasksToDisplay.length === 0 && title === "Completed Tasks" && (
            <div className="text-xs text-muted-foreground text-center py-2">
              No completed tasks for today
            </div>
          )}

          {tasksToDisplay.length === 0 && title === "Today's Task" && (
            <div className="text-xs text-muted-foreground text-center py-2">
              No tasks for today
            </div>
          )}
          
          {title !== "Completed Tasks" && title !== "Today's Task" && (
            <div className="text-xs text-muted-foreground text-center py-2">
              Click to view details
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
